import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import qs from "query-string";

import dayjs from "dayjs";
import Select from "react-select";

import {
  QUERY_TWILIO_USAGE_RECORDS,
  QUERY_TWILIO_USAGE_REPORT,
} from "../../config/graphql/query";

import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

import { reactSelectCustomStyles } from "../Employees/Employee/Information";

/**
 * Twilio usage — phase 1, measurement only.
 *
 * Reports on the client selected in the CMS header and nothing else. No price anywhere:
 * the one amount shown is `twilioCost`, what Twilio charged *us*, which must never reach
 * a client.
 */

interface ITwilioUsageCost {
  amount?: number | null;
  currency?: string | null;
}

interface ITwilioUsageTotals {
  records: number;
  seconds: number;
  startedMinutes: number;
  segments: number;
  pingRecords: number;
  unconnectedRecords: number;
  twilioCost?: ITwilioUsageCost | null;
}

interface ITwilioUsageZoneTotals {
  zone: string;
  kind: "call" | "sms";
  totals: ITwilioUsageTotals;
}

interface ITwilioUsageCountryTotals {
  country?: string | null;
  kind: "call" | "sms";
  totals: ITwilioUsageTotals;
}

/**
 * Beyond a handful of destinations the country table stops pointing at where the money
 * goes and becomes a second copy of the raw log.
 */
const COUNTRY_LIMIT = 8;

interface ITwilioUsageRecord {
  id: string;
  kind: "call" | "sms";
  occurredAt: string;
  seconds?: number | null;
  segments?: number | null;
  zone: string;
  destination: string;
  country?: string | null;
  status?: string | null;
  errorCode?: number | null;
  ping: boolean;
  source: string;
  twilioCost?: ITwilioUsageCost | null;
}

interface IRecordsQuery {
  twilioUsageRecords: {
    count: number;
    records: ITwilioUsageRecord[];
  };
}

const PERIOD_COUNT = 13;

const RECORD_LIMIT = 10;

/** One query parameter per list, so paging the calls does not drag the messages along. */
const PAGE_PARAM = { call: "callPage", sms: "smsPage" } as const;

interface ISearchQuery {
  callPage?: number;
  smsPage?: number;
}

/** Same list and same order as `TWILIO_USAGE_ZONES` on the server. */
const ZONES = ["nl-fixed", "nl-mobile", "premium", "international", "unknown"];

/**
 * The zone is a closed set, so its table shows every row every month even at zero — the
 * empty rows are what say `premium` is being watched and that `unknown` would appear if a
 * number failed to parse.
 */
const emptyTotals = (): ITwilioUsageTotals => ({
  records: 0,
  seconds: 0,
  startedMinutes: 0,
  segments: 0,
  pingRecords: 0,
  unconnectedRecords: 0,
  twilioCost: null,
});

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;

  return `${minutes}m ${String(rest).padStart(2, "0")}s`;
};

/**
 * Twilio rates a call minutes after it ends, never in the status callback, and reports the
 * price as a negative (what it deducted from the balance). So an unrated row shows `—`,
 * not `0.00` — "not priced yet" and "cost us nothing" are different answers — and the
 * column prints the magnitude. Mongo keeps the value exactly as Twilio sent it.
 */
const formatCost = (cost?: ITwilioUsageCost | null) => {
  if (cost?.amount === undefined || cost?.amount === null) {
    return "—";
  }

  return `${Math.abs(cost.amount).toFixed(2)} ${cost.currency || ""}`.trim();
};

const Stat = React.memo(
  (props: {
    label: string;
    value: React.ReactNode;
    hint?: string | null;
    first?: boolean;
  }) => (
    <div className={`col px-3 ${props.first ? "" : "border-left"}`}>
      <div className="text-muted text-uppercase small">{props.label}</div>
      <div className="h4 mb-0 text-truncate">{props.value}</div>
      <div className="small text-muted">{props.hint || " "}</div>
    </div>
  ),
);

/**
 * Zone and country breakdowns share one table. What changes with the kind is the quantity
 * counted: seconds and started minutes for calls, segments for messages, never both.
 */
const BreakdownTable = React.memo(
  (props: {
    kind: "call" | "sms";
    head: string;
    rows: { key: string; label: string; totals: ITwilioUsageTotals }[];
    /** Show each row's share of the month's records. */
    share?: boolean;
    empty: string;
    className?: string;
  }) => {
    const { t } = useTranslation(["twilioUsage", "common"]);

    const { kind, head, rows, share, empty, className } = props;

    const isCall = kind === "call";

    const total = Math.max(
      1,
      rows.reduce((sum, row) => sum + row.totals.records, 0),
    );

    const columns = isCall ? 5 : 4;

    return (
      <Table size="sm" className={className}>
        <thead>
          <tr>
            <th scope="col">{head}</th>
            <th scope="col" className="text-right">
              {isCall
                ? t("twilioUsage:calls.count")
                : t("twilioUsage:sms.count")}
            </th>
            {isCall ? (
              <>
                <th scope="col" className="text-right">
                  {t("twilioUsage:calls.talkTime")}
                </th>
                <th scope="col" className="text-right">
                  {t("twilioUsage:th.startedMinutes")}
                </th>
              </>
            ) : (
              <th scope="col" className="text-right">
                {t("twilioUsage:sms.segments")}
              </th>
            )}
            <th scope="col" className="text-right">
              {t("twilioUsage:twilioCost")}
            </th>
          </tr>
        </thead>
        <tbody>
          {!rows.length ? (
            <tr>
              <td colSpan={columns} className="text-muted">
                {empty}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td className="text-right text-nowrap">
                {/* Brackets so the count and its share cannot run together when the row
                    is copied out. Dropped at zero: a column of "0 (0%)" is noise. */}
                {row.totals.records}
                {share && row.totals.records ? (
                  <span className="text-muted ml-1">
                    ({Math.round((row.totals.records / total) * 100)}%)
                  </span>
                ) : null}
              </td>
              {isCall ? (
                <>
                  <td className="text-right text-nowrap">
                    {formatDuration(row.totals.seconds)}
                  </td>
                  <td className="text-right">{row.totals.startedMinutes}</td>
                </>
              ) : (
                <td className="text-right">{row.totals.segments}</td>
              )}
              <td className="text-right text-nowrap">
                {formatCost(row.totals.twilioCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  },
);

/**
 * Bars scale to the busiest bucket, percentages to the whole month. The track is drawn even
 * at zero, otherwise an empty row reads as a rendering fault rather than a measurement.
 */
const Histogram = React.memo(
  (props: {
    rows: { key: string; label: string; value: number; muted?: boolean }[];
  }) => {
    const max = Math.max(1, ...props.rows.map((row) => row.value));

    const total = Math.max(
      1,
      props.rows.reduce((sum, row) => sum + row.value, 0),
    );

    return (
      <>
        {props.rows.map((row) => (
          <div key={row.key} className="d-flex align-items-center mb-2">
            <div
              className="small text-right text-nowrap mr-2"
              style={{ width: "4.5rem" }}
            >
              {row.label}
            </div>
            <div
              className="flex-grow-1 rounded bg-light"
              style={{ height: "0.75rem" }}
            >
              <div
                className={`rounded ${
                  row.muted ? "bg-secondary" : "bg-primary"
                }`}
                style={{
                  width: `${(row.value / max) * 100}%`,
                  height: "100%",
                }}
              />
            </div>
            <div
              className="small text-right text-nowrap ml-2"
              style={{ width: "5.5rem" }}
            >
              {row.value ? (
                <>
                  <strong>{row.value}</strong>
                  <span className="text-muted ml-1">
                    {Math.round((row.value / total) * 100)}%
                  </span>
                </>
              ) : (
                <span className="text-muted">0</span>
              )}
            </div>
          </div>
        ))}
      </>
    );
  },
);

/**
 * The raw rows, under the totals they add up to. Split per kind so each table can drop the
 * columns the other needs — no "Type" column, and one quantity column holding one unit.
 */
const RecordsPanel = React.memo(
  (props: {
    kind: "call" | "sms";
    data?: IRecordsQuery;
    zone?: string;
    onZone: (zone?: string) => void;
    zoneOptions: { value: string; label: string }[];
    zoneLabel: (value: string) => string;
  }) => {
    const { t } = useTranslation(["twilioUsage", "common"]);

    const { kind, data, zone, onZone, zoneOptions, zoneLabel } = props;

    const records = data?.twilioUsageRecords.records || [];

    const count = data?.twilioUsageRecords.count ?? 0;

    const isCall = kind === "call";

    return (
      <>
        <div className="d-flex flex-wrap align-items-center mt-4">
          <h6 className="text-uppercase text-muted small mb-0 mr-auto">
            {isCall
              ? t("twilioUsage:records.callsTitle")
              : t("twilioUsage:records.smsTitle")}
          </h6>

          <div style={{ width: "14rem" }}>
            <Select
              inputId={`zone-${kind}`}
              isClearable
              placeholder={t("twilioUsage:records.filter.all")}
              options={zoneOptions}
              value={
                zoneOptions.find((option) => option.value === zone) || null
              }
              onChange={(option) => onZone(option?.value)}
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>
        <p className="text-muted small mt-2">
          {isCall
            ? t("twilioUsage:records.callsHint")
            : t("twilioUsage:records.smsHint")}{" "}
          {t("twilioUsage:records.count", {
            shown: records.length,
            total: count,
          })}
        </p>

        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th scope="col">{t("twilioUsage:records.th.occurredAt")}</th>
              <th scope="col">{t("twilioUsage:records.th.destination")}</th>
              <th scope="col">{t("twilioUsage:th.zone")}</th>
              <th scope="col" className="text-right">
                {isCall
                  ? t("twilioUsage:records.th.duration")
                  : t("twilioUsage:records.th.segments")}
              </th>
              <th scope="col">{t("twilioUsage:records.th.status")}</th>
              <th scope="col" className="text-right">
                {t("twilioUsage:twilioCost")}
              </th>
              <th scope="col">{t("twilioUsage:records.th.source")}</th>
            </tr>
          </thead>
          <tbody>
            {data && !records.length ? (
              <tr>
                <td colSpan={7} className="text-muted">
                  {zone
                    ? t("twilioUsage:records.emptyFiltered")
                    : t("twilioUsage:empty")}
                </td>
              </tr>
            ) : null}
            {records.map((record) => (
              <tr key={record.id}>
                <td className="text-nowrap">
                  {dayjs(record.occurredAt).local().format("DD-MM HH:mm")}
                  {record.ping ? (
                    <span className="badge badge-warning ml-1">ping</span>
                  ) : null}
                </td>
                <td className="text-nowrap">
                  {record.destination}
                  {record.country ? (
                    <span className="text-muted small ml-1">
                      ({record.country})
                    </span>
                  ) : null}
                </td>
                <td>{zoneLabel(record.zone)}</td>
                <td className="text-right text-nowrap">
                  {isCall
                    ? formatDuration(record.seconds ?? 0)
                    : record.segments ?? 0}
                </td>
                <td>
                  {/* Only the outcomes that never got through are coloured. */}
                  <span
                    className={`badge ${
                      record.status === "completed" ||
                      record.status === "delivered"
                        ? "badge-light"
                        : "badge-warning"
                    }`}
                  >
                    {record.status || "—"}
                    {record.errorCode ? ` (${record.errorCode})` : ""}
                  </span>
                </td>
                <td className="text-right text-nowrap">
                  {formatCost(record.twilioCost)}
                </td>
                <td className="small text-muted">{record.source}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {count > RECORD_LIMIT ? (
          <Pagination
            documentsCount={count}
            limit={RECORD_LIMIT}
            param={PAGE_PARAM[kind]}
          />
        ) : null}
      </>
    );
  },
);

const TwilioUsage = React.memo(() => {
  const { t } = useTranslation(["twilioUsage", "common"]);

  const periodOptions = useMemo(
    () =>
      Array.from({ length: PERIOD_COUNT }).map((_, index) => {
        const value = dayjs().subtract(index, "month").format("YYYY-MM");

        return { value, label: value };
      }),
    [],
  );

  /**
   * ⚠️ `useLocation`, not `useHistory().location`: the history context value never changes
   * identity, and this page is mounted as a `children` element, so paging would update the
   * URL and re-render nothing.
   */
  const { push } = useHistory();
  const location = useLocation();

  const query = useMemo(
    () => qs.parse(location.search, { parseNumbers: true }) as ISearchQuery,
    [location.search],
  );

  const pageOf = (kind: "call" | "sms") =>
    Math.max(Number(query[PAGE_PARAM[kind]]) || 1, 1);

  /** Anything that changes what the list contains sends it back to page one. */
  const resetPages = (next: Partial<ISearchQuery> = {}) =>
    push({
      search: qs.stringify({
        ...query,
        ...next,
        [PAGE_PARAM.call]: undefined,
        [PAGE_PARAM.sms]: undefined,
      }),
    });

  const [period, setPeriod] = useState(periodOptions[0].value);

  // Separate on purpose: narrowing the calls while reading every SMS is a normal thing
  // to want, and a shared filter would move the other column too.
  const [callZone, setCallZone] = useState<string | undefined>();
  const [smsZone, setSmsZone] = useState<string | undefined>();

  const { data, loading } = useQuery<{
    twilioUsageReport: {
      client?: { id: string; title: string } | null;
      calls: ITwilioUsageTotals;
      sms: ITwilioUsageTotals;
      byZone: ITwilioUsageZoneTotals[];
      byCountry: ITwilioUsageCountryTotals[];
      durationBuckets: {
        label: string;
        fromSeconds: number;
        toSeconds?: number | null;
        calls: number;
      }[];
      segmentBuckets: {
        label: string;
        fromSegments: number;
        messages: number;
      }[];
    };
  }>(QUERY_TWILIO_USAGE_REPORT, {
    fetchPolicy: "network-only",
    variables: { period },
  });

  const { data: callRecordsData } = useQuery<IRecordsQuery>(
    QUERY_TWILIO_USAGE_RECORDS,
    {
      fetchPolicy: "network-only",
      variables: {
        period,
        kind: "call",
        zone: callZone,
        limit: RECORD_LIMIT,
        skip: (pageOf("call") - 1) * RECORD_LIMIT,
      },
    },
  );

  const { data: smsRecordsData } = useQuery<IRecordsQuery>(
    QUERY_TWILIO_USAGE_RECORDS,
    {
      fetchPolicy: "network-only",
      variables: {
        period,
        kind: "sms",
        zone: smsZone,
        limit: RECORD_LIMIT,
        skip: (pageOf("sms") - 1) * RECORD_LIMIT,
      },
    },
  );

  const report = data?.twilioUsageReport;

  const calls = report?.calls;
  const sms = report?.sms;

  /** `t()` is typed as possibly null; one helper guarantees the string and the fallback. */
  const label = (
    key: string,
    fallback: string,
    values?: Record<string, unknown>,
  ): string => {
    const translated = t(key, { defaultValue: fallback, ...values });

    return typeof translated === "string" ? translated : fallback;
  };

  const zoneLabel = (value: string) =>
    label(`twilioUsage:zone.${value}`, value);

  /** Codes, not names: that is what a Twilio price list shows on the other screen. */
  const countryLabel = (value?: string | null) =>
    value || label("twilioUsage:country.unknown", "?");

  const breakdowns = (kind: "call" | "sms") => {
    const zones = new Map(
      (report?.byZone || [])
        .filter((entry) => entry.kind === kind)
        .map((entry) => [entry.zone, entry.totals]),
    );

    const countries = (report?.byCountry || []).filter(
      (entry) => entry.kind === kind,
    );

    return {
      // Every zone, in the server's order, landed in this month or not.
      zones: ZONES.map((zone) => ({
        key: zone,
        label: zoneLabel(zone),
        totals: zones.get(zone) || emptyTotals(),
      })),
      countries: countries.slice(0, COUNTRY_LIMIT).map((entry) => ({
        key: entry.country || "unknown",
        label: countryLabel(entry.country),
        totals: entry.totals,
      })),
      /** Countries that were dialled this month but did not fit in the table. */
      countriesHidden: Math.max(0, countries.length - COUNTRY_LIMIT),
    };
  };

  const callBreakdowns = breakdowns("call");
  const smsBreakdowns = breakdowns("sms");

  const durationBuckets = report?.durationBuckets || [];

  // The `0` bucket only catches messages with no segment count, which `sendSms` never
  // writes — hidden rather than shown as a permanently blank row.
  const segmentBuckets = (report?.segmentBuckets || []).filter(
    (bucket) => bucket.fromSegments > 0 || bucket.messages > 0,
  );

  const zoneOptions = ZONES.map((value) => ({
    value,
    label: zoneLabel(value),
  }));

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("twilioUsage:nav.title")}
            {report?.client ? ` — ${report.client.title}` : ""}
          </li>
        </ol>
      </nav>

      <div className="d-flex align-items-center mb-4">
        <label htmlFor="period" className="mb-0 mr-2">
          {t("twilioUsage:filter.period")}
        </label>
        <div style={{ width: "12rem" }}>
          <Select
            inputId="period"
            options={periodOptions}
            value={periodOptions.find((option) => option.value === period)}
            onChange={(option) => {
              setPeriod(option?.value || period);
              resetPages();
            }}
            styles={reactSelectCustomStyles(false)}
          />
        </div>
        {loading ? (
          <span className="ml-3 text-muted">{t("common:loading")}</span>
        ) : null}
      </div>

      <div className="row">
        <div className="col-xl-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">{t("twilioUsage:calls.title")}</h5>
            </div>

            <div className="row no-gutters py-3 border-bottom">
              <Stat
                first
                label={t("twilioUsage:calls.count")}
                value={calls?.records ?? 0}
                hint={t("twilioUsage:calls.ping", {
                  count: calls?.pingRecords ?? 0,
                })}
              />
              <Stat
                label={t("twilioUsage:calls.talkTime")}
                value={formatDuration(calls?.seconds ?? 0)}
                hint={t("twilioUsage:calls.startedMinutes", {
                  count: calls?.startedMinutes ?? 0,
                })}
              />
              <Stat
                label={t("twilioUsage:calls.unconnected")}
                value={calls?.unconnectedRecords ?? 0}
                hint={t("twilioUsage:calls.unconnectedHint")}
              />
              <Stat
                label={t("twilioUsage:twilioCost")}
                value={formatCost(calls?.twilioCost)}
              />
            </div>

            <div className="card-body">
              <h6 className="text-uppercase text-muted small">
                {t("twilioUsage:th.zone")}
              </h6>
              <BreakdownTable
                kind="call"
                head={t("twilioUsage:th.zone")}
                rows={callBreakdowns.zones}
                share
                empty={t("twilioUsage:empty")}
                className="mb-4"
              />

              <h6 className="text-uppercase text-muted small">
                {t("twilioUsage:country.title")}
              </h6>
              <BreakdownTable
                kind="call"
                head={t("twilioUsage:th.country")}
                rows={callBreakdowns.countries}
                empty={t("twilioUsage:empty")}
                className={callBreakdowns.countriesHidden ? "mb-1" : "mb-4"}
              />
              {callBreakdowns.countriesHidden ? (
                <p className="text-muted small mb-4">
                  {t("twilioUsage:country.more", {
                    count: callBreakdowns.countriesHidden,
                  })}
                </p>
              ) : null}

              <h6 className="text-uppercase text-muted small">
                {t("twilioUsage:histogram.title")}
              </h6>
              <Histogram
                rows={durationBuckets.map((bucket) => ({
                  key: bucket.label,
                  // Nobody picked up — not a duration, so it is greyed out rather than
                  // read as the shortest conversation.
                  label:
                    bucket.fromSeconds === 0 && bucket.toSeconds === 1
                      ? "0s"
                      : bucket.label,
                  value: bucket.calls,
                  muted: bucket.fromSeconds === 0 && bucket.toSeconds === 1,
                }))}
              />

              <RecordsPanel
                kind="call"
                data={callRecordsData}
                zone={callZone}
                onZone={(zone) => {
                  setCallZone(zone);
                  resetPages();
                }}
                zoneOptions={zoneOptions}
                zoneLabel={zoneLabel}
              />
            </div>
          </div>
        </div>

        <div className="col-xl-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">{t("twilioUsage:sms.title")}</h5>
            </div>

            <div className="row no-gutters py-3 border-bottom">
              <Stat
                first
                label={t("twilioUsage:sms.count")}
                value={sms?.records ?? 0}
              />
              <Stat
                label={t("twilioUsage:sms.segments")}
                value={sms?.segments ?? 0}
                hint={t("twilioUsage:sms.segmentsHint")}
              />
              <Stat
                label={t("twilioUsage:twilioCost")}
                value={formatCost(sms?.twilioCost)}
              />
            </div>

            <div className="card-body">
              <h6 className="text-uppercase text-muted small">
                {t("twilioUsage:th.zone")}
              </h6>
              <BreakdownTable
                kind="sms"
                head={t("twilioUsage:th.zone")}
                rows={smsBreakdowns.zones}
                share
                empty={t("twilioUsage:empty")}
                className="mb-4"
              />

              <h6 className="text-uppercase text-muted small">
                {t("twilioUsage:country.title")}
              </h6>
              <BreakdownTable
                kind="sms"
                head={t("twilioUsage:th.country")}
                rows={smsBreakdowns.countries}
                empty={t("twilioUsage:empty")}
                className={smsBreakdowns.countriesHidden ? "mb-1" : "mb-4"}
              />
              {smsBreakdowns.countriesHidden ? (
                <p className="text-muted small mb-4">
                  {t("twilioUsage:country.more", {
                    count: smsBreakdowns.countriesHidden,
                  })}
                </p>
              ) : null}

              <h6 className="text-uppercase text-muted small mt-4">
                {t("twilioUsage:segments.title")}
              </h6>
              <Histogram
                rows={segmentBuckets.map((bucket) => ({
                  key: bucket.label,
                  label: label("twilioUsage:segments.bucket", bucket.label, {
                    label: bucket.label,
                  }),
                  value: bucket.messages,
                  muted: bucket.fromSegments === 0,
                }))}
              />

              <RecordsPanel
                kind="sms"
                data={smsRecordsData}
                zone={smsZone}
                onZone={(zone) => {
                  setSmsZone(zone);
                  resetPages();
                }}
                zoneOptions={zoneOptions}
                zoneLabel={zoneLabel}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TwilioUsage;
